import {
    type ApiEndpoint,
    type DownloadUrlResponse,
    isDownloadUrlResponse,
} from "@/shared/api-endpoint";
import type { ParameterRange } from "@/shared/parameter-range";
import { isObject } from "@/shared/utils/typeGuards";

export const kInpaintingMagicFitRoute = "/inpainting-magic-fit";

// Defaults mirror `InpaintingMagicFitInput` in audio-science-genai
// (`serving/apps/inpainting_magic_fit/app.py`) so the UI opens on the same
// values the model falls back to when a field is omitted. Complexity is shared
// with Variations 3.0 / Inpainting via `kComplexity30Range`.
export const kVariationRange: ParameterRange = { min: 0, max: 1, default: 0.13 };
export const kBalanceRange: ParameterRange = { min: 0, max: 1, default: 0.5 };

export interface InpaintingMagicFitRequestBody {
    drivingAudioKey: string;
    timbreAudioKey: string;
    bpm: number;
    variation?: number;
    balance?: number;
    complexity?: number;
    // Absolute seconds into the model context window. The masked [start, end]
    // range of the timbre audio is regenerated; everything outside it survives
    // into the output. Omitting the pair is the purely generative case.
    inpaintMaskStartSeconds?: number;
    inpaintMaskEndSeconds?: number;
}

export const isInpaintingMagicFitRequestBody = (
    value: unknown,
): value is InpaintingMagicFitRequestBody =>
    isObject(value) &&
    typeof value.drivingAudioKey === "string" &&
    typeof value.timbreAudioKey === "string" &&
    typeof value.bpm === "number";

export const kInpaintingMagicFitDescription = "Fill loops to fit another sample";

export const kInpaintingMagicFitEndpoint: ApiEndpoint<
    InpaintingMagicFitRequestBody,
    DownloadUrlResponse
> = {
    route: kInpaintingMagicFitRoute,
    label: "Inpainting Magic Fit",
    source: "Modal",
    isEvaluationOnly: true,
    category: "SpliceGen v3.5 fill",
    description: kInpaintingMagicFitDescription,
    isRequestBody: isInpaintingMagicFitRequestBody,
    isSuccessResponse: isDownloadUrlResponse,
};
